<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import CommonUtils from '@/utils/common-utils';
import { useWidgetRangeStore } from '~/store/widgetRangeStore';

const props = defineProps<{
  apiData: any;
  property?: {
    title?: string;
    isRow?: boolean;
    tableData?: Array<{
      name: string;
      key: string;
      unit?: string;
    }>;
    syncWithRange?: boolean;
  };
  callApiFn?: (params?: Record<string, any>) => void;
}>();

const commonUtils = CommonUtils;
const widgetRangeStore = useWidgetRangeStore();
const shouldSyncWithRange = computed(() => props.property?.syncWithRange === true);


const selectCid = computed(() => widgetRangeStore.selectCid)

// API 데이터 추출 (배열인 경우 첫 번째 요소, 객체인 경우 그대로 사용)
const data = computed(() => {
  if (Array.isArray(props.apiData)) {
    return props.apiData[0] ?? null;
  }
  return props.apiData ?? null;
});

// 테이블 데이터 설정
const tableData = computed(() => {
  return props.property?.tableData ?? [];
});

// 테이블 제목
const title = computed(() => {
  return props.property?.title ?? '';
});

const rangeTitle = computed(() => {
  if(widgetRangeStore.activeRange === 'day' ) {
    return '금일';
  } else if( widgetRangeStore.activeRange === 'month' ) {
    return '금월';
  } else if( widgetRangeStore.activeRange === 'year' ) {
    return '금년';
  }
  return '';
})

// API 데이터에서 값을 가져오는 함수
function getValue(key: string) {
  if (!data.value) return '-';
  return data.value[key] ?? '-';
}

function getDisplayValue(key: string) {
  const value = getValue(key);
  if (value === '-') return '-';

  if (key === 'periodTotalUsageKWH' || key === 'totalUsageKWH' || key === 'totalProdKWH' || key === 'totalCpTotalUsageKWH' || key === 'totalHpTotalUsageKWH') {
    return value / 1000;
  }

  return value;
}

function syncRangeToApi() {
  console.log('range sync');
  if (!shouldSyncWithRange.value || !props.callApiFn) {
    return;
  }
  // console.log(widgetRangeStore.currentRangeParams);

  props.callApiFn({
    ...widgetRangeStore.currentRangeParams
  });
}

onMounted(() => {
  if (shouldSyncWithRange.value) {
    syncRangeToApi();
  }
});

watch(
  () => widgetRangeStore.activeRange,
  () => {
    if (shouldSyncWithRange.value) {
      syncRangeToApi();
    }
  }
);

watch(
  () => shouldSyncWithRange.value,
  (newVal) => {
    if (newVal) {
      syncRangeToApi();
    }
  }
);

watch(
  selectCid,
  (newVal) => {
    if(newVal){
      syncRangeToApi();
    }
  }
);

</script>

<template>
  <div class="base-table common-card">
    <label class="table-label">지열시스템 누적데이터</label>
    <div class="content h-100">
      <div class="table-wrapper h-100">
        <table class="data-table" :style="{height: '85%'}" v-if="props.property?.isRow === false">
          <colgroup>
            <col width="60%">
            <col width="40%">
          </colgroup>
          <thead>
            <tr v-if="title">
              <th colspan="2">
                <template v-if="shouldSyncWithRange">
                  {{ rangeTitle }} {{ title }}
                </template>
                <template v-else>
                  {{ title }}
                </template>
              </th>
            </tr>
     
          </thead>
          <tbody>
            <tr v-for="(item, index) in tableData" :key="index">
              <td>{{ item.name }}{{ item.unit ? ` (${item.unit})` : '' }}</td>
              <td class="value-cell">{{ commonUtils.isAllStrDataEmpty(getDisplayValue(item.key), 3) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="split-tables" v-else>
          <!-- 왼쪽: 에너지 생산량 -->
          <table class="cum-table left-table" v-if="tableData[0]">
            <tbody>
              <tr>
                <td class="header-cell">
                  {{ tableData[0].name }}{{ tableData[0].unit ? ` (${tableData[0].unit})` : '' }}
                </td>
              </tr>
              <tr>
                <td class="value-cell">
                  {{ commonUtils.isAllStrDataEmpty(getDisplayValue(tableData[0].key), 3) }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- 오른쪽: 소비 전력 (헤더 + 합계 | 히트/순환 세부) -->
          <div class="right-card" v-if="tableData[1]">
            <div class="right-header">
              {{ tableData[1].name }}{{ tableData[1].unit ? ` (${tableData[1].unit})` : '' }}
            </div>
            <div class="right-body">
              <div class="total-value">
                {{ commonUtils.isAllStrDataEmpty(getDisplayValue(tableData[1].key), 3) }}
              </div>
              <table class="breakdown-table">
                <tbody>
                  <tr v-for="item in tableData.slice(2)" :key="item.key">
                    <td class="label-cell">{{ item.name }}</td>
                    <td class="value-cell">
                      {{ commonUtils.isAllStrDataEmpty(getDisplayValue(item.key), 3) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/design-toc/define_font.scss";
@import "@/assets/styles/design-toc/common-card.scss";

.base-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 !important;

  .table-label{
    @include text-bold-14;
    padding: 4px 10px 0; // 상단 여백 축소
    flex-shrink: 0;
  }

  .split-tables {
    display: flex;
    align-items: flex-start; // 카드가 위젯 높이로 늘어나지 않음
    gap: 8px;
    width: 93%;
    margin: 10px auto 0; // 테이블 아래로 조금 이동
    padding: 2px 8px 4px;
    box-sizing: border-box;
    flex: 1;
    min-height: 0;
    height: auto;

    .left-table {
      flex: 1;
      align-self: flex-start;
      height: auto;
      display: flex;
      flex-direction: column;

      tbody {
        display: flex;
        flex-direction: column;
        flex: 0 0 auto;
        height: auto;
      }

      tr:first-child {
        flex: 0 0 auto;
        height: auto;
      }

      // 2번째 tr (값) 높이
      tr:last-child {
        flex: 0 0 2.8rem;
        height: 2.8rem;
        min-height: 2.8rem;
        max-height: 2.8rem;
      }

      td {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .header-cell {
        height: auto;
        padding: 0.25rem 0.5rem;
      }

      .value-cell {
        height: 100%;
        padding: 0.1rem 0.5rem;
      }
    }

    .right-card {
      flex: 2;
      display: flex;
      flex-direction: column;
      border: 1px solid #e5e5e5;
      border-radius: 6px;
      overflow: hidden;
      min-height: 0;
      height: fit-content;
      align-self: flex-start; // stretch로 늘어나지 않게
      flex-grow: 2;
      flex-shrink: 0;
    }

    .cum-table {
      width: 100%;
      height: auto;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: fixed;

      td {
        border: none;
        border-right: 1px solid #e5e5e5;
        border-bottom: 1px solid #e5e5e5;
        border-left: 1px solid #e5e5e5;
        font-size: 13px;
        padding: 0.2rem 0.5rem;
        text-align: center;
        vertical-align: middle;
        color: #464A53;
        box-sizing: border-box;
      }

      tr:first-child td {
        border-top: 1px solid #e5e5e5;
        border-top-left-radius: 6px;
        border-top-right-radius: 6px;
      }

      tr:last-child td {
        border-bottom-left-radius: 6px;
        border-bottom-right-radius: 6px;
      }

      .header-cell {
        background-color: #8FDBF8;
        font-weight: bold;
      }

      .value-cell {
        background-color: #fff;
        color: #007CE1;
        font-size: 15px;
        font-weight: 600;
      }
    }

    .right-header {
      background-color: #8FDBF8;
      border-bottom: 1px solid #e5e5e5;
      text-align: center;
      font-size: 13px;
      font-weight: bold;
      color: #464A53;
      padding: 0.25rem 0.5rem;
      flex: 0 0 auto; // % 고정 제거 → 상위 행 축소
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
    }

    .right-body {
      display: flex;
      flex: 0 0 2.8rem !important;
      height: 2.8rem !important;
      min-height: 2.8rem !important;
      max-height: 2.8rem !important;
      background: #fff;
      align-items: stretch;
      padding: 0;
      box-sizing: border-box;
      overflow: hidden;
    }

    .total-value {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #007CE1;
      font-size: 15px;
      font-weight: 600;
      border-right: 1px solid #e5e5e5;
      background: #fff;
      padding: 0;
    }

    .breakdown-table {
      flex: 1.2;
      width: 100%;
      height: 100%;
      border-collapse: collapse;
      table-layout: fixed;

      tbody {
        height: 100%;
      }

      tr {
        height: 50%; // 2.4rem 안에서 히트/순환 분배
      }

      td {
        border: 1px solid #e5e5e5;
        font-size: 12px;
        padding: 0 0.4rem;
        line-height: 1.1;
        height: 50%;
        vertical-align: middle;
        box-sizing: border-box;
      }

      // 바깥쪽과 겹치는 border 제거
      tr td:first-child {
        border-left: none;
      }

      tr td:last-child {
        border-right: none;
      }

      tr:first-child td {
        border-top: none;
      }

      tr:last-child td {
        border-bottom: none;
      }

      .label-cell {
        width: 45%;
        text-align: center;
        background-color: rgb(202, 238, 251);
        color: #464A53;
        font-weight: 600;
      }

      .value-cell {
        width: 55%;
        text-align: center;
        padding-right: 0.4rem;
        background-color: #fff;
        color: #007CE1;
        font-size: 14px;
        font-weight: 600;
      }
    }
  }

  .content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .table-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-radius: 6px;
    min-height: 0;
    overflow: hidden; // 잘림 대신 내부 flex로 높이 분배
  }

  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 12px;
    display: table;
    table-layout: fixed; // 고정 레이아웃
    // 높이는 화면 크기에 맞게 자동 조절 (내용보다 화면 크기 우선)

    thead {
      background: #EFF6FF;
      display: table-header-group;
      flex-shrink: 0;

      th {
        @include text-normal-12;
        font-size: 13px;
        padding: 0.4rem 0.5rem;
        text-align: center;
        color: #464A53;
        font-weight: 700;
        border: none;
        border-bottom: 1px solid #ddd;
      }
    }

    tbody {
      display: table-row-group;
      // 높이는 화면 크기에 맞게 자동 조절

      tr {
        background: transparent;
        display: table-row;
        // 높이는 화면 크기에 맞게 자동 조절 (최소화 가능)

        &:nth-child(even) {
          background: transparent;
        }
      }

      td {
        @include text-normal-12;
        font-size: 13px;
        padding: 0.15rem 0.3rem; // 최소 padding으로 공간 절약
        text-align: center;
        color: #464A53;
        border: none;
        border-bottom: 1px solid #e5e5e5;
        display: table-cell;
        vertical-align: middle;
        line-height: 1.2; // 줄 간격 최소화
        height: auto; // 내용에 맞게 자동 조절
        // 화면이 작아지면 높이가 최소화됨
        white-space: normal; // 텍스트 줄바꿈 허용
        word-break: break-word; // 긴 단어 줄바꿈

        &:first-child {
          text-align: left;
          font-weight: 500;
        }

        &.value-cell {
          color: #007CE1;
          font-size: 15px;
          font-weight: 600;
        }
      }
    }
  }
}
</style>
